// Class lists and small markup helpers shared by the CRM's Tailwind screens (#1198), taken from Tailwind Plus with its
// indigo turned into paprika. Use these instead of copying a component's classes again, so every
// screen's buttons, badges, fields and cards match. Every list is a whole string: Tailwind finds
// class names by reading this file as text, so never build one from pieces.
//
// White text goes on paprika-600 or darker (#C4472F, 4.9:1), in both themes.

export const BUTTON = {
  primary:
    "inline-flex items-center justify-center gap-x-1.5 rounded-md bg-paprika-600 px-3 py-2 text-sm font-semibold text-white shadow-xs " +
    "hover:bg-paprika-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paprika-600 " +
    "disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-none dark:focus-visible:outline-paprika-500",
  secondary:
    "inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs " +
    "inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 " +
    "dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20",
  // Secondary with red text, for actions that stop or pause something (#1211).
  secondaryDanger:
    "inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-xs " +
    "inset-ring inset-ring-gray-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 " +
    "dark:bg-white/10 dark:text-red-400 dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-red-500/10",
  danger:
    "inline-flex items-center justify-center gap-x-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs " +
    "hover:bg-red-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 " +
    "disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-none",
  // Small secondary, for row actions in tables and bulk bars.
  small:
    "inline-flex items-center gap-x-1 rounded-sm bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-xs " +
    "inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 " +
    "dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/10 dark:hover:bg-white/15",
  smallPrimary:
    "inline-flex items-center gap-x-1 rounded-sm bg-paprika-600 px-2 py-1 text-sm font-semibold text-white shadow-xs " +
    "hover:bg-paprika-700 disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-none",
  smallDanger:
    "inline-flex items-center gap-x-1 rounded-sm bg-white px-2 py-1 text-sm font-semibold text-red-600 shadow-xs " +
    "inset-ring inset-ring-gray-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30 " +
    "dark:bg-white/10 dark:text-red-400 dark:shadow-none dark:inset-ring-white/10 dark:hover:bg-red-500/10",
  success:
    "inline-flex items-center justify-center gap-x-1.5 rounded-md bg-green-700 px-3 py-2 text-sm font-semibold text-white shadow-xs " +
    "hover:bg-green-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-700 " +
    "disabled:cursor-not-allowed disabled:opacity-50 dark:shadow-none",
  // A square icon-only button that removes something (a row, a task).
  iconDanger:
    "inline-flex flex-none items-center justify-center rounded-md p-1.5 text-red-600 hover:bg-red-50 " +
    "focus-visible:outline-2 focus-visible:outline-red-600 dark:text-red-400 dark:hover:bg-red-500/10",
  // Text-only, for links inside tables ("Edit").
  link: "font-semibold text-paprika-600 hover:text-paprika-800 dark:text-paprika-400 dark:hover:text-paprika-300"
};

// Pill badges ("flat with border"); a dot variant adds BADGE_DOT before the label.
export const BADGE = {
  gray: "inline-flex items-center gap-x-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 inset-ring inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20",
  green: "inline-flex items-center gap-x-1.5 rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 inset-ring inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20",
  yellow: "inline-flex items-center gap-x-1.5 rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 inset-ring inset-ring-yellow-600/20 dark:bg-yellow-400/10 dark:text-yellow-500 dark:inset-ring-yellow-400/20",
  red: "inline-flex items-center gap-x-1.5 rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 inset-ring inset-ring-red-600/10 dark:bg-red-400/10 dark:text-red-400 dark:inset-ring-red-400/20",
  blue: "inline-flex items-center gap-x-1.5 rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 inset-ring inset-ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:inset-ring-blue-400/30",
  paprika: "inline-flex items-center gap-x-1.5 rounded-md bg-paprika-50 px-2 py-1 text-xs font-medium text-paprika-700 inset-ring inset-ring-paprika-600/20 dark:bg-paprika-500/10 dark:text-paprika-300 dark:inset-ring-paprika-400/30"
};
export const BADGE_DOT = {
  gray: "size-1.5 rounded-full bg-gray-400",
  green: "size-1.5 rounded-full bg-green-500",
  yellow: "size-1.5 rounded-full bg-yellow-500",
  red: "size-1.5 rounded-full bg-red-500",
  blue: "size-1.5 rounded-full bg-blue-500",
  paprika: "size-1.5 rounded-full bg-paprika-500"
};

export const FIELD = {
  label: "block text-sm/6 font-medium text-gray-900 dark:text-white",
  input:
    "block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 " +
    "placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-paprika-600 sm:text-sm/6 " +
    "dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-paprika-500",
  // A native <select>; put SELECT_CHEVRON after it, both inside a `grid grid-cols-1` wrapper.
  select:
    "col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 " +
    "-outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-paprika-600 sm:text-sm/6 " +
    "dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800 dark:focus-visible:outline-paprika-500",
  selectChevron: "pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4 dark:text-gray-400",
  // Compact versions for dense inline editors (rows of ingredients, steps).
  inputSm:
    "block w-full rounded-md bg-white px-2 py-1 text-sm/6 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 " +
    "placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-paprika-600 " +
    "dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-paprika-500",
  selectSm:
    "block w-full rounded-md bg-white py-1 pr-7 pl-2 text-sm/6 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 " +
    "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-paprika-600 " +
    "dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800 dark:focus-visible:outline-paprika-500",
  labelSm: "block text-xs/5 font-medium text-gray-700 dark:text-gray-300",
  help: "mt-2 text-sm text-gray-500 dark:text-gray-400",
  error: "mt-2 text-sm text-red-600 dark:text-red-400"
};

export const CARD = {
  panel: "overflow-hidden rounded-lg bg-white shadow-xs outline-1 outline-black/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10",
  body: "px-4 py-5 sm:p-6",
  title: "text-base font-semibold text-gray-900 dark:text-white",
  description: "mt-1 text-sm text-gray-500 dark:text-gray-400"
};

export const TABLE = {
  table: "min-w-full divide-y divide-gray-300 dark:divide-white/15",
  th: "px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white",
  tbody: "divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900",
  td: "px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400",
  tdStrong: "px-3 py-4 text-sm font-medium whitespace-nowrap text-gray-900 dark:text-white"
};

export const EMPTY_STATE = {
  box: "rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-white/15",
  icon: "mx-auto size-12 text-gray-400 dark:text-gray-500",
  title: "mt-2 text-sm font-semibold text-gray-900 dark:text-white",
  text: "mt-1 text-sm text-gray-500 dark:text-gray-400"
};

// A segmented filter: buttons side by side, the chosen one with aria-pressed="true".
export const SEGMENT = {
  group: "isolate inline-flex rounded-md shadow-xs dark:shadow-none",
  button:
    "relative -ml-px inline-flex items-center gap-x-1.5 bg-white px-3 py-2 text-sm font-semibold text-gray-900 inset-ring-1 inset-ring-gray-300 " +
    "first:ml-0 first:rounded-l-md last:rounded-r-md hover:bg-gray-50 focus:z-10 " +
    "aria-pressed:z-10 aria-pressed:bg-gray-900 aria-pressed:text-white aria-pressed:inset-ring-gray-900 " +
    "dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20 " +
    "dark:aria-pressed:bg-white dark:aria-pressed:text-gray-900 dark:aria-pressed:inset-ring-white",
  count:
    "rounded-full bg-gray-100 px-1.5 text-xs font-medium text-gray-600 in-aria-pressed:bg-white/15 in-aria-pressed:text-white " +
    "dark:bg-white/10 dark:text-gray-300 dark:in-aria-pressed:bg-gray-900/10 dark:in-aria-pressed:text-gray-900"
};

// A pill that toggles the radio or checkbox inside it (sr-only), dark while chosen.
export const PILL =
  "inline-flex cursor-pointer items-center rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-200 select-none ring-inset " +
  "hover:bg-gray-100 has-checked:bg-gray-900 has-checked:text-white has-checked:ring-gray-900 " +
  "has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-paprika-600 " +
  "dark:bg-white/5 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/10 dark:has-checked:bg-white dark:has-checked:text-gray-900 dark:has-checked:ring-white";

// The Tailwind Plus checkbox: a styled native input with a check and a dash drawn over it.
const CHECKBOX_INPUT =
  "col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-paprika-600 checked:bg-paprika-600 " +
  "indeterminate:border-paprika-600 indeterminate:bg-paprika-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paprika-600 " +
  "disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 dark:border-white/20 dark:bg-gray-800/50 " +
  "dark:checked:border-paprika-500 dark:checked:bg-paprika-500 dark:indeterminate:border-paprika-500 dark:indeterminate:bg-paprika-500 " +
  "dark:focus-visible:outline-paprika-500 forced-colors:appearance-auto";

/** A checkbox; `attrs` is the input's other attributes as written in HTML (id, data-*, onchange, checked). */
export function checkbox(attrs = "") {
  return `<div class="group grid size-4 grid-cols-1"><input type="checkbox" class="${CHECKBOX_INPUT}" ${attrs}><svg viewBox="0 0 14 14" fill="none" aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25"><path d="M3 8L6 11L11 3.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-checked:opacity-100" /><path d="M3 7H11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="opacity-0 group-has-indeterminate:opacity-100" /></svg></div>`;
}
